import { defaultFilterCriteria } from "../components/SubmitTip/resources";
import { FetchedReportData, FilterCriteria } from "../types";

/**
 * Helper function to check if two values are effectively empty
 * (either both undefined, or one is undefined and the other is an empty string)
 */
const isEffectivelyEmpty = (val1: any, val2: any): boolean => {
  // Both are undefined or null
  if (
    (val1 === undefined || val1 === null) &&
    (val2 === undefined || val2 === null)
  ) {
    return true;
  }

  // One is empty string and one is undefined/null
  if (
    (val1 === "" && (val2 === undefined || val2 === null)) ||
    (val2 === "" && (val1 === undefined || val1 === null))
  ) {
    return true;
  }

  // Both are empty strings
  if (val1 === "" && val2 === "") {
    return true;
  }

  // Values are the same
  return val1 === val2;
};

/**
 * Checks if the current filterCriteria is effectively equal to the defaultFilterCriteria
 * (meaning no filters have been set)
 *
 * @param criteria The current filter criteria to check
 * @returns boolean - true if no filters have been set, false otherwise
 */
export const isDefaultFilterCriteria = (criteria: FilterCriteria): boolean => {
  // Check main top-level fields
  if (!isEffectivelyEmpty(criteria.caseType, defaultFilterCriteria.caseType))
    return false;
  if (
    !isEffectivelyEmpty(criteria.subCaseType, defaultFilterCriteria.subCaseType)
  )
    return false;
  if (!isEffectivelyEmpty(criteria.location, defaultFilterCriteria.location))
    return false;
  if (
    !isEffectivelyEmpty(
      criteria.reportStatus,
      defaultFilterCriteria.reportStatus
    )
  )
    return false;

  // Check date object
  if (criteria.date.type !== defaultFilterCriteria.date.type) return false;
  if (criteria.date.mode !== defaultFilterCriteria.date.mode) return false;
  if (
    !isEffectivelyEmpty(
      criteria.date.startDate,
      defaultFilterCriteria.date.startDate
    )
  )
    return false;
  if (
    !isEffectivelyEmpty(
      criteria.date.endDate,
      defaultFilterCriteria.date.endDate
    )
  )
    return false;

  // Check detailed location
  if (
    !isEffectivelyEmpty(
      criteria.detailedIncidentLocation.state,
      defaultFilterCriteria.detailedIncidentLocation.state
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.detailedIncidentLocation.city,
      defaultFilterCriteria.detailedIncidentLocation.city
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.detailedIncidentLocation.county,
      defaultFilterCriteria.detailedIncidentLocation.county
    )
  )
    return false;

  // Check reporter details
  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.role,
      defaultFilterCriteria.reporterDetails.role
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.gender,
      defaultFilterCriteria.reporterDetails.gender
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.age,
      defaultFilterCriteria.reporterDetails.age
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.phone,
      defaultFilterCriteria.reporterDetails.phone
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.email,
      defaultFilterCriteria.reporterDetails.email
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.reporterDetails.ethnicity,
      defaultFilterCriteria.reporterDetails.ethnicity
    )
  )
    return false;

  // Check abuser info
  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.name,
      defaultFilterCriteria.abuserInfo.name
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.alias,
      defaultFilterCriteria.abuserInfo.alias
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.gender,
      defaultFilterCriteria.abuserInfo.gender
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.refAge,
      defaultFilterCriteria.abuserInfo.refAge
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.relation,
      defaultFilterCriteria.abuserInfo.relation
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.occupation,
      defaultFilterCriteria.abuserInfo.occupation
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.ethnicity,
      defaultFilterCriteria.abuserInfo.ethnicity
    )
  )
    return false;

  if (
    criteria.abuserInfo.hasSpecialAuthority !==
    defaultFilterCriteria.abuserInfo.hasSpecialAuthority
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.authorityPosition,
      defaultFilterCriteria.abuserInfo.authorityPosition
    )
  )
    return false;

  if (
    !isEffectivelyEmpty(
      criteria.abuserInfo.phyDescription,
      defaultFilterCriteria.abuserInfo.phyDescription
    )
  )
    return false;

  if (
    criteria.abuserInfo.hasAbuserVehicleInfo !==
    defaultFilterCriteria.abuserInfo.hasAbuserVehicleInfo
  )
    return false;

  // Check vehicle info if it exists
  if (
    criteria.abuserInfo.vehicleInfo &&
    defaultFilterCriteria.abuserInfo.vehicleInfo
  ) {
    if (
      !isEffectivelyEmpty(
        criteria.abuserInfo.vehicleInfo.brand,
        defaultFilterCriteria.abuserInfo.vehicleInfo.brand
      )
    )
      return false;

    if (
      !isEffectivelyEmpty(
        criteria.abuserInfo.vehicleInfo.model,
        defaultFilterCriteria.abuserInfo.vehicleInfo.model
      )
    )
      return false;

    if (
      !isEffectivelyEmpty(
        criteria.abuserInfo.vehicleInfo.carColor,
        defaultFilterCriteria.abuserInfo.vehicleInfo.carColor
      )
    )
      return false;

    if (
      !isEffectivelyEmpty(
        criteria.abuserInfo.vehicleInfo.licensePlate,
        defaultFilterCriteria.abuserInfo.vehicleInfo.licensePlate
      )
    )
      return false;
  }

  // Check support services
  if (
    criteria.supportServices.NeedAnAttorney !==
    defaultFilterCriteria.supportServices.NeedAnAttorney
  )
    return false;

  if (
    criteria.supportServices.NeedCounseling !==
    defaultFilterCriteria.supportServices.NeedCounseling
  )
    return false;

  if (
    criteria.supportServices.NeedPrivateInvestigator !==
    defaultFilterCriteria.supportServices.NeedPrivateInvestigator
  )
    return false;

  // If all checks pass, the criteria is equal to default
  return true;
};

// Helper function to search across all properties of a report
export const searchReportProperties = (
  report: FetchedReportData,
  searchQuery: string
): boolean => {
  // Convert search query to lowercase for case-insensitive matching
  const query = searchQuery.toLowerCase();

  // Helper function to recursively search through nested objects
  const searchObject = (obj: any): boolean => {
    if (!obj) return false;

    // If it's an array, search through each element
    if (Array.isArray(obj)) {
      return obj.some((item) => {
        if (typeof item === "object") return searchObject(item);
        if (typeof item === "string") return item.toLowerCase().includes(query);
        if (item !== null && item !== undefined)
          return String(item).toLowerCase().includes(query);
        return false;
      });
    }

    // If it's an object, search through each property
    if (typeof obj === "object") {
      return Object.values(obj).some((value) => {
        if (typeof value === "object") return searchObject(value);
        if (typeof value === "string")
          return value.toLowerCase().includes(query);
        if (value !== null && value !== undefined)
          return String(value).toLowerCase().includes(query);
        return false;
      });
    }

    // If it's a string, check if it matches the query
    if (typeof obj === "string") return obj.toLowerCase().includes(query);

    // If it's another primitive, convert to string and check
    if (obj !== null && obj !== undefined)
      return String(obj).toLowerCase().includes(query);

    return false;
  };

  // Search through the entire report object
  return searchObject(report);
};
